import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Check, CheckCircle2, Clock, Copy, Download, Eye, FileText, HelpCircle, Loader2, Mail, Paperclip, RefreshCw, Send, X } from 'lucide-react';
import { Layout } from '../components/Layout';
import { MisActionConfirm, type MisConfirmAction } from '../components/MisActionConfirm';
import { apiClient } from '../api/client';

function ticketId(id: string) {
  return `CG${id.slice(0, 7).toUpperCase()}`;
}

const MIS_FILE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'xls', 'xlsx', 'doc', 'docx', 'pdf']);
const MIS_MAX_FILE_BYTES = 15 * 1024 * 1024;

function fileExtension(name: string) {
  const parts = name.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function adminStatusBadge(idea: any) {
  let displayStatus = String(idea.status || '').replace(/_/g, ' ');
  let displayColor = 'bg-slate-100 text-slate-700 border border-slate-200';
  if (idea.status === 'SUBMITTED') displayStatus = 'YET TO BE REVIEWED';
  else if (idea.status === 'UNDER_REVIEW') {
    displayStatus = 'REVIEWED';
    displayColor = 'bg-blue-50 text-blue-700 border border-blue-200';
  } else if (idea.status === 'PENDING_EXTERNAL_APPROVAL') displayStatus = 'REQUESTED EC HEAD APPROVAL';
  else if (idea.status === 'APPROVED') displayColor = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  else if (idea.status === 'REJECTED') displayColor = 'bg-red-50 text-red-700 border border-red-200';
  else if (idea.status === 'MORE_INFO_REQUESTED') {
    displayStatus = 'HOLD';
    displayColor = 'bg-amber-50 text-amber-700 border border-amber-200';
  } else if (idea.status === 'WORK_IN_PROGRESS') displayStatus = 'WIP';
  else if (idea.status === 'PENDING_HEAD_APPROVAL') displayStatus = 'PENDING IC HEAD APPROVAL';
  else if (idea.status === 'DUPLICATE') displayStatus = 'REJECT (DUPLICATE)';
  return { displayStatus, displayColor };
}

function renderAdminTimelineItem(log: any) {
  const actionType = log.action ? log.action.replace(/^(COMMITTEE_MEMBER_|COMMITTEE_HEAD_|EXTERNAL_COMMITTEE_|ADMIN_)/, '') : '';
  let target = log.statusTo || log.action || '';
  if (actionType === 'APPROVED' || actionType === 'EVALUATED') target = actionType;

  let colorClass = 'bg-blue-500 ring-blue-100 text-blue-700';
  let icon = <Send className="w-3 h-3 text-white" />;
  if (target === 'REJECTED') {
    colorClass = 'bg-red-500 ring-red-100 text-red-700';
    icon = <X className="w-3.5 h-3.5 text-white" />;
  } else if (target === 'APPROVED' || target === 'EVALUATED') {
    colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
    icon = <Check className="w-3.5 h-3.5 text-white" />;
  } else if (log.action?.includes('UPDATED')) {
    colorClass = 'bg-indigo-500 ring-indigo-100 text-indigo-700';
    icon = <RefreshCw className="w-3 h-3 text-white" />;
  } else if (target === 'MORE_INFO_REQUESTED') {
    colorClass = 'bg-amber-500 ring-amber-100 text-amber-700';
    icon = <HelpCircle className="w-3.5 h-3.5 text-white" />;
  } else if (target === 'DUPLICATE') {
    colorClass = 'bg-slate-500 ring-slate-100 text-slate-700';
    icon = <Copy className="w-3.5 h-3.5 text-white" />;
  } else if (String(target).startsWith('PENDING') || target === 'UNHOLD' || String(target).includes('UNREJECT')) {
    colorClass = 'bg-orange-500 ring-orange-100 text-orange-700';
    icon = <Clock className="w-3 h-3 text-white" />;
  } else if (target === 'UNDER_REVIEW' || target === 'DEPARTMENT_CHANGED') {
    colorClass = 'bg-indigo-500 ring-indigo-100 text-indigo-700';
    icon = <RefreshCw className="w-3 h-3 text-white" />;
  }

  let displayAction = String(log.action || '').replace(/^(COMMITTEE_MEMBER_|COMMITTEE_HEAD_|EXTERNAL_COMMITTEE_|ADMIN_)/, '').replace(/_/g, ' ');
  if (String(log.action || '').includes('MORE_INFO_REQUESTED')) displayAction = 'HOLD';
  if (displayAction === 'SUBMITTED' || displayAction === 'IDEA SUBMITTED') displayAction = 'YET TO BE REVIEWED';
  else if (displayAction === 'UNDER REVIEW') displayAction = 'REVIEWED';
  else if (displayAction === 'PENDING EXTERNAL APPROVAL') displayAction = 'REQUESTED EC HEAD APPROVAL';

  const resumeStatuses = ['UNDER_REVIEW', 'PENDING_HEAD_APPROVAL', 'PENDING_EXTERNAL_APPROVAL'];
  if (log.statusFrom === 'REJECTED' && resumeStatuses.includes(log.statusTo || '')) {
    displayAction = 'UNREJECTED';
    colorClass = 'bg-rose-500 ring-rose-100 text-rose-700';
    icon = <RefreshCw className="w-3 h-3 text-white" />;
  } else if (log.statusFrom === 'MORE_INFO_REQUESTED' && resumeStatuses.includes(log.statusTo || '')) {
    displayAction = 'UNHOLD';
    colorClass = 'bg-orange-500 ring-orange-100 text-orange-700';
    icon = <RefreshCw className="w-3 h-3 text-white" />;
  } else if (log.statusFrom === 'WORK_IN_PROGRESS' && log.statusTo === 'SUBMITTED') {
    displayAction = 'UN-WIP';
    colorClass = 'bg-slate-500 ring-slate-100 text-slate-700';
    icon = <RefreshCw className="w-3 h-3 text-white" />;
  } else if (log.statusTo === 'WORK_IN_PROGRESS') {
    displayAction = 'WIP';
  } else if (log.action === 'EVALUATED' || log.action === 'EVALUATION_UPDATED' || String(log.action || '').endsWith('VALIDATED') || String(log.action || '').endsWith('VALIDATION_UPDATED')) {
    const actorTeam = log.actor?.subCoreRole ? `${String(log.actor.subCoreRole).replace(/_/g, ' ')} TEAM` : 'COST TEAM';
    if (String(log.action).includes('EVALUATED') || String(log.action).includes('EVALUATION')) {
      displayAction = log.action === 'EVALUATED' ? `EVALUATED BY ${actorTeam}` : `EVALUATION UPDATED BY ${actorTeam}`;
    } else {
      displayAction = String(log.action).endsWith('VALIDATION_UPDATED') ? 'VALIDATION UPDATED BY COST CENTRE MEMBER TEAM' : 'VALIDATED BY COST CENTRE MEMBER TEAM';
    }
    colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
    icon = <Check className="w-3.5 h-3.5 text-white" />;
  } else if (log.statusTo === 'APPROVED' && log.statusFrom === 'PENDING_EXTERNAL_APPROVAL') {
    displayAction = 'APPROVED BY EXTERNAL COMMITTEE';
    colorClass = 'bg-emerald-500 ring-emerald-100 text-emerald-700';
    icon = <Check className="w-3.5 h-3.5 text-white" />;
  } else if (log.action === 'DEPARTMENT_CHANGED') {
    displayAction = 'DEPARTMENT CHANGED';
  }

  const isUpdate = String(log.action || '').includes('UPDATED') || String(log.action || '').includes('EDITED');
  if (isUpdate) {
    return (
      <div key={log.id} className="relative text-xs py-1 mt-2 mb-4 group">
        <div className="absolute -left-[28px] top-[17px] w-2.5 h-2.5 rounded-full border-[2px] border-indigo-400 bg-white ring-4 ring-white z-10" />
        <div className="absolute top-[21px] -left-[23px] w-[28px] h-[1.5px] bg-slate-200 z-0" />
        <div className="bg-white border border-slate-100 rounded-xl p-3.5 ml-2 relative shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <p className="text-indigo-600 text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5 text-indigo-400" />
              {displayAction}
            </p>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">{new Date(log.createdAt).toLocaleString('en-GB')}</p>
          </div>
          {log.actor?.name && (
            <div className="flex items-center gap-2 mt-2.5">
              <div className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-600">
                {log.actor.name.charAt(0)}
              </div>
              <p className="text-[11px] font-medium text-slate-500">
                <span className="text-slate-700 font-semibold">{log.actor.name}</span>
                <span className="text-slate-400 ml-1">({log.actor.role ? String(log.actor.role).replace(/_/g, ' ') : ''})</span>
              </p>
            </div>
          )}
          {log.comment && (
            <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-2.5 relative overflow-hidden">
              <p className="text-slate-600 font-medium italic text-[11px] leading-relaxed pl-1.5 whitespace-pre-line">{log.comment}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div key={log.id} className="relative text-xs mb-6">
      <div className={`absolute -left-[35px] top-0 w-6 h-6 rounded-full flex items-center justify-center ring-4 ${colorClass.split(' text-')[0]}`}>
        {icon}
      </div>
      {target === 'APPROVED' ? (
        <div className="inline-flex items-center bg-emerald-500 text-white px-2.5 py-0.5 rounded shadow-sm text-[11px] font-bold tracking-wider uppercase mb-1">
          {displayAction}
        </div>
      ) : (
        <p className={`${colorClass.split(' ')[2]} text-xs flex items-center font-bold tracking-wide`}>{displayAction}</p>
      )}
      {log.actor?.name && (
        <p className="font-medium text-slate-500 mt-0.5">
          by {log.actor.role ? `${String(log.actor.role).replace(/_/g, ' ')} ` : ''}{log.actor.name}
        </p>
      )}
      <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">{new Date(log.createdAt).toLocaleString('en-GB')}</p>
      {log.comment && <p className="text-slate-600 mt-2 font-medium italic leading-relaxed bg-slate-50/80 p-3 rounded-lg border border-slate-200 whitespace-pre-line">{log.comment}</p>}
    </div>
  );
}

export function MisIdeaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<{ id: string; file: File; previewUrl: string }[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState<MisConfirmAction | null>(null);
  const [undoDescription, setUndoDescription] = useState('');
  const [showFullLog, setShowFullLog] = useState(false);

  const { data: idea, isLoading, isError } = useQuery({
    queryKey: ['misIdea', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await apiClient.get(`/admin/mis/ideas/${id}`);
      return res.data.data;
    },
  });

  useEffect(() => {
    if (idea?.misReview?.misDescription) {
      setDescription(idea.misReview.misDescription);
    }
  }, [idea?.misReview?.misDescription]);

  const decisionMutation = useMutation({
    mutationFn: async ({ misStatus, undoDescription: reason }: { misStatus: MisConfirmAction; undoDescription?: string }) => {
      const files = [];
      for (const pending of pendingFiles) {
        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: { fileName: pending.file.name, contentType: pending.file.type || 'application/octet-stream' },
        });
        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, pending.file, { headers: { 'Content-Type': pending.file.type || 'application/octet-stream' } });
        files.push({
          fileName: pending.file.name,
          fileKey,
          fileSize: pending.file.size,
          fileType: pending.file.type || 'application/octet-stream',
        });
      }
      const res = misStatus === 'UNAPPROVE' || misStatus === 'UNREJECT'
        ? await apiClient.post(`/admin/mis/ideas/${id}/decision`, { misStatus, misDescription: reason, files })
        : await apiClient.post(`/admin/mis/ideas/${id}/decision`, {
          misStatus,
          misDescription: description.trim(),
          files,
        });
      for (const fileId of removedFileIds) {
        await apiClient.delete(`/admin/mis/ideas/${id}/files/${fileId}`);
      }
      return res.data.data;
    },
    onSuccess: () => {
      pendingFiles.forEach((pending) => URL.revokeObjectURL(pending.previewUrl));
      setPendingFiles([]);
      setRemovedFileIds([]);
      setConfirmAction(null);
      setUndoDescription('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['misIdea', id] });
      queryClient.invalidateQueries({ queryKey: ['misIdeas'] });
      queryClient.invalidateQueries({ queryKey: ['misStats'] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || 'Could not save the MIS decision.');
    },
  });

  const askConfirm = (misStatus: MisConfirmAction) => {
    if (decisionMutation.isPending) return;
    if ((misStatus === 'APPROVED' || misStatus === 'REJECTED') && description.trim().length < 3) {
      setError('Enter a description of at least 3 characters.');
      return;
    }
    setError('');
    setUndoDescription('');
    setConfirmAction(misStatus);
  };

  const confirmSubmit = () => {
    if (!confirmAction || decisionMutation.isPending) return;
    if ((confirmAction === 'UNAPPROVE' || confirmAction === 'UNREJECT') && undoDescription.trim().length < 3) {
      setError('Enter a description of at least 3 characters.');
      return;
    }
    setError('');
    decisionMutation.mutate({ misStatus: confirmAction, undoDescription: undoDescription.trim() });
  };

  if (isLoading) {
    return (
      <Layout title="MIS Idea">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (isError || !idea) {
    return (
      <Layout title="MIS Idea">
        <button type="button" onClick={() => navigate('/mis')} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back to MIS Portal
        </button>
        <p className="text-sm text-slate-600">This idea is not in the MIS queue.</p>
      </Layout>
    );
  }

  const evaluation = idea.evaluations?.[0];
  const validation = idea.validation;
  const submissionAttachments = (idea.attachments || []).filter((fileItem: any) => !fileItem.source || fileItem.source === 'SUBMISSION');
  const readOnlyInput = 'w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-700 text-xs font-semibold outline-none';
  const statusBadge = adminStatusBadge(idea);
  const renderTimeline = () => (idea.timelines || []).map((entry: any) => renderAdminTimelineItem(entry));

  return (
    <Layout title="MIS Idea">
      <button
        type="button"
        onClick={() => navigate('/mis')}
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" /> Back to MIS Portal
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold tracking-wide border border-indigo-100">{ticketId(idea.id)}</span>
                <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-bold tracking-wide border border-slate-200">
                  <span className="opacity-75 font-semibold mr-1">Department:</span>
                  <span className="uppercase">{idea.department}</span>
                </span>
                <span className={`text-xs px-3 py-1 rounded-full font-bold border ${statusBadge.displayColor}`}>{statusBadge.displayStatus}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900 break-all">{idea.title}</h1>
              <p className="text-sm text-slate-500 mt-2">Submitted by {idea.employee?.name}</p>
            </div>
            <hr className="border-slate-100" />
            <div>
              <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-3">Description / Solution</h3>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line break-words">{idea.description}</p>
            </div>
            {idea.costSaving != null && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Indicative Saving</span>
                <p className="text-base font-bold text-emerald-600 mt-1">₹{Number(idea.costSaving).toLocaleString('en-IN')}</p>
              </div>
            )}
          </div>

          {submissionAttachments.length > 0 && (
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold font-display text-slate-800 mb-4">Uploaded Attachments</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {submissionAttachments.map((fileItem: any) => (
                  <div key={fileItem.id} className="bg-slate-50 border border-slate-200 p-4 rounded-xl flex items-center justify-between">
                    <div className="min-w-0 pr-2">
                      <p className="text-xs font-semibold text-slate-700 truncate">{fileItem.fileName}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{(fileItem.fileSize / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a href={fileItem.downloadUrl || fileItem.fileUrl} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-600 p-2 bg-white border border-slate-200 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </a>
                      <a href={fileItem.downloadUrl || fileItem.fileUrl} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600 p-2 bg-white border border-slate-200 rounded-lg">
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-6 rounded-xl border border-teal-200 bg-white shadow-md flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs uppercase font-bold tracking-wider text-teal-700">MIS Decision</h3>
              {idea.misReview?.misStatus && (
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${idea.misReview.misStatus === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {idea.misReview.misStatus === 'APPROVED' ? 'MIS Approved' : 'MIS Rejected'}
                  {idea.misReview.decidedBy?.name ? ` · ${idea.misReview.decidedBy.name}` : ''}
                </span>
              )}
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Description</label>
              <textarea
                rows={8}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Decision notes"
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] text-slate-500 font-bold uppercase">Attachments</label>
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-teal-100">
                  <Paperclip className="w-3.5 h-3.5" />
                  Add files
                  <input
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      const accepted: { id: string; file: File; previewUrl: string }[] = [];
                      const rejected: string[] = [];
                      selected.forEach((selectedFile) => {
                        const extension = fileExtension(selectedFile.name);
                        if (!MIS_FILE_EXTENSIONS.has(extension)) {
                          rejected.push(`${selectedFile.name} is not allowed. Use PNG, JPG, JPEG, Excel, Word, or PDF.`);
                          return;
                        }
                        if (selectedFile.size > MIS_MAX_FILE_BYTES) {
                          rejected.push(`${selectedFile.name} is larger than 15 MB.`);
                          return;
                        }
                        accepted.push({
                          id: `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`,
                          file: selectedFile,
                          previewUrl: URL.createObjectURL(selectedFile),
                        });
                      });
                      if (accepted.length > 0) {
                        setPendingFiles((current) => [...current, ...accepted]);
                      }
                      setError(rejected.join(' '));
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              <p className="text-[10px] text-slate-400 mb-2">PNG, JPG, JPEG, Excel, Word, PDF. Max 15 MB each.</p>
              <div className="space-y-2">
                {(idea.misReview?.files || []).filter((savedFile: any) => !removedFileIds.includes(savedFile.id)).map((savedFile: any) => (
                  <div key={savedFile.id || savedFile.fileKey} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{savedFile.fileName}</p>
                      <p className="text-[10px] text-slate-400">{savedFile.fileSize ? `${(savedFile.fileSize / 1024).toFixed(0)} KB` : 'Saved'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => window.open(savedFile.downloadUrl || savedFile.fileUrl, '_blank', 'noopener,noreferrer')}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50 shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemovedFileIds((current) => current.includes(savedFile.id) ? current : [...current, savedFile.id])}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {pendingFiles.map((pending) => (
                  <div key={pending.id} className="flex items-center justify-between gap-3 bg-teal-50/60 border border-teal-100 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{pending.file.name}</p>
                      <p className="text-[10px] text-slate-400">{(pending.file.size / 1024).toFixed(0)} KB · not saved yet</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => window.open(pending.previewUrl, '_blank', 'noopener,noreferrer')}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(pending.previewUrl);
                          setPendingFiles((current) => current.filter((item) => item.id !== pending.id));
                        }}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(idea.misReview?.files || []).filter((savedFile: any) => !removedFileIds.includes(savedFile.id)).length === 0 && pendingFiles.length === 0 && (
                  <p className="text-xs text-slate-400">No files attached.</p>
                )}
              </div>
            </div>
            {error && <p className="text-red-500 text-xs font-semibold">{error}</p>}
            <p className="self-start text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">Note: Reject is considered a duplicate.</p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              {idea.misReview?.misStatus === 'APPROVED' || idea.misReview?.misStatus === 'REJECTED' ? (
                (() => {
                  const undoStatus = idea.misReview.misStatus === 'APPROVED' ? 'UNAPPROVE' : 'UNREJECT';
                  const undoLabel = idea.misReview.misStatus === 'APPROVED' ? 'Unapprove' : 'Unreject';
                  const isThisLoading = decisionMutation.isPending && decisionMutation.variables?.misStatus === undoStatus;
                  return (
                    <button
                      type="button"
                      disabled={isThisLoading}
                      onClick={() => askConfirm(undoStatus)}
                      style={{ backgroundColor: 'oklch(0.62 0.14 55)' }}
                      className="relative inline-flex items-center justify-center w-[6.75rem] h-7 rounded-lg text-white text-[10px] font-bold uppercase tracking-wide hover:brightness-95 disabled:cursor-wait"
                    >
                      <span className={isThisLoading ? 'invisible' : ''}>{undoLabel}</span>
                      {isThisLoading && <Loader2 className="absolute w-3.5 h-3.5 animate-spin" />}
                    </button>
                  );
                })()
              ) : (
                ([
                  ['REJECTED', 'Reject', 'oklch(0.62 0.18 22.45)'],
                  ['APPROVED', 'Approve', 'oklch(0.58 0.11 165.46)'],
                ] as const).map(([status, label, color]) => {
                  const isThisLoading = decisionMutation.isPending && decisionMutation.variables?.misStatus === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      disabled={isThisLoading}
                      onClick={() => askConfirm(status)}
                      style={{ backgroundColor: color }}
                      className="relative inline-flex items-center justify-center w-[5.25rem] h-7 rounded-lg text-white text-[10px] font-bold uppercase tracking-wide hover:brightness-95 disabled:cursor-wait"
                    >
                      <span className={isThisLoading ? 'invisible' : ''}>{label}</span>
                      {isThisLoading && <Loader2 className="absolute w-3.5 h-3.5 animate-spin" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="p-6 rounded-xl border border-emerald-300 bg-emerald-50/30 shadow-md space-y-4">
            <h3 className="text-xs uppercase font-bold tracking-wider font-display flex items-center space-x-2 text-emerald-700">
              <span>Evaluate Idea (Completed)</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            </h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 font-bold text-slate-600">Criteria</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['1) Saving Potential', !!evaluation?.savingPotential],
                    ['2) Ease of Implementation', !!evaluation?.easeOfImplementation],
                    ['3) Time of Implementation', !!evaluation?.timeOfImplementation],
                  ].map(([label, checked]) => (
                    <tr key={label as string}>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                            {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                          </div>
                          <span className="font-semibold text-emerald-800">{label}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(evaluation?.category || idea.evaluationCategory) && (
              <div>
                <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Calculated Category</label>
                <div className="inline-flex items-center justify-center px-4 py-2 bg-indigo-50 text-indigo-700 font-bold rounded-lg border border-indigo-100 text-sm">
                  Category {evaluation?.category || idea.evaluationCategory}
                </div>
              </div>
            )}
            <div>
              <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Comments</label>
              <textarea
                readOnly
                rows={4}
                value={evaluation?.comments || ''}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-800 text-xs"
              />
            </div>
          </div>

          {validation && (
            <div className="p-6 rounded-xl border border-emerald-300 bg-emerald-50/30 shadow-md space-y-4">
              <h3 className="text-xs uppercase font-bold tracking-wider font-display flex items-center space-x-2 text-emerald-700">
                <span>Validation & Accrual (Completed)</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ['1. Current Cost', validation.currentCost],
                  ['3. Saving Type', validation.savingType],
                  ['4. Annual Savings', validation.annualSavings],
                  ['5. One Time Investment', validation.oneTimeInvestment],
                  ['6. Owner', validation.owner],
                  ['7. Implementation Timeline', validation.implementationTimeline],
                ].map(([label, value]) => (
                  <div key={label}>
                    <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">{label}</label>
                    <input readOnly value={value || ''} className={readOnlyInput} />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">2. Propose Solution</label>
                  <textarea readOnly rows={3} value={validation.proposeSolution || ''} className={readOnlyInput} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">8. Risk</label>
                  <textarea readOnly rows={3} value={validation.risk || ''} className={readOnlyInput} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">9. Notes</label>
                  <textarea readOnly rows={3} value={validation.notes || ''} className={readOnlyInput} />
                </div>
              </div>
            </div>
          )}

          {['PENDING_EXTERNAL_APPROVAL', 'APPROVED', 'EXECUTION_APPROVED', 'PENDING_EXECUTION_APPROVAL'].includes(idea.status) && (
            <div className="w-full bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-md">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-slate-200 p-2 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-700" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Email Confirmation</h3>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium">Head approval details and confirmation documents</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">Annual Savings Amount (₹)</label>
                    <input readOnly value={idea.emailConfirmation?.approvedAmount || ''} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-xs outline-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">Confirmation Date</label>
                    <input
                      readOnly
                      value={idea.emailConfirmation?.confirmationDate
                        ? new Date(idea.emailConfirmation.confirmationDate).toISOString().substring(0, 10)
                        : idea.emailConfirmation?.createdAt
                          ? new Date(idea.emailConfirmation.createdAt).toISOString().substring(0, 10)
                          : ''}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-xs outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">Reason</label>
                  <textarea readOnly rows={3} value={idea.emailConfirmation?.reason || ''} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-xs outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">Attachments (Optional)</label>
                  {(idea.attachments || []).filter((fileItem: any) => fileItem.source === 'EMAIL_HEAD_APPROVAL').length === 0 ? (
                    <p className="text-xs text-slate-400">No files attached.</p>
                  ) : (
                    <div className="flex flex-col space-y-2">
                      {(idea.attachments || []).filter((fileItem: any) => fileItem.source === 'EMAIL_HEAD_APPROVAL').map((fileItem: any) => (
                        <div key={fileItem.id} className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg text-xs">
                          <div className="flex items-center space-x-2 overflow-hidden">
                            <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="font-medium text-slate-700 truncate">{fileItem.fileName}</span>
                          </div>
                          <a href={fileItem.downloadUrl || fileItem.fileUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-500 hover:text-blue-600">
                            <Eye className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {idea.status === 'PENDING_EXTERNAL_APPROVAL' && (
            <div className="w-full bg-orange-50 p-6 rounded-xl border border-orange-200 shadow-md">
              <div className="flex items-start space-x-3">
                <Loader2 className="w-5 h-5 text-orange-600 animate-spin mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-orange-800 tracking-wide">Waiting for External Committee Approval</h3>
                  <p className="text-xs text-orange-700 mt-1 font-medium">This idea is currently pending external validation.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">Idea Submitter</h3>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center font-display text-blue-700 font-bold">
                {idea.employee?.name?.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{idea.employee?.name}</p>
                <p className="text-xs text-slate-500 truncate">{idea.employee?.email}</p>
              </div>
            </div>
            <hr className="border-slate-100" />
            <div className="space-y-2.5 text-xs">
              {[
                ['Employee ID', idea.employee?.mpId || 'N/A'],
                ['Department', idea.employee?.department || 'N/A'],
                ['Designation', idea.employee?.designation || 'N/A'],
                ['Mobile Number', idea.employee?.mobile || 'N/A'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-slate-500 font-medium">{label}</span>
                  <span className="text-slate-800 font-semibold">{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs uppercase font-bold text-slate-500 tracking-wider">Submission Status</h3>
              <button
                type="button"
                onClick={() => setShowFullLog(true)}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors flex items-center space-x-1"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>View Log</span>
              </button>
            </div>
            <div className="flex items-center space-x-3.5 mb-5">
              <span className={`text-sm uppercase font-bold tracking-wider px-3.5 py-1.5 rounded-full ${statusBadge.displayColor}`}>
                {statusBadge.displayStatus}
              </span>
            </div>
            <div className="space-y-6 border-l-2 border-slate-100 pl-6 relative ml-2 mt-4">
              {renderTimeline()}
            </div>
          </div>

          {showFullLog && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
              <div className="bg-slate-50 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
                <div className="p-6 bg-white border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 font-display">Detailed Idea Log</h3>
                    <p className="text-xs text-slate-500 font-medium">Complete history of updates and approvals</p>
                  </div>
                  <button type="button" onClick={() => setShowFullLog(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-8 overflow-y-auto flex-1">
                  <div className="space-y-6 border-l-2 border-slate-200 pl-6 relative ml-2">
                    {renderTimeline()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {confirmAction && (
        <MisActionConfirm
          action={confirmAction}
          description={undoDescription}
          onDescriptionChange={setUndoDescription}
          error={error}
          pending={decisionMutation.isPending}
          onCancel={() => {
            if (decisionMutation.isPending) return;
            setConfirmAction(null);
            setError('');
          }}
          onConfirm={confirmSubmit}
        />
      )}
    </Layout>
  );
}
